import React, {useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import appConfig from 'config/app.config';
import apiHelper from 'helpers/api-helper';
import {shuffleArray} from 'helpers/array-helper';
import {loginUiTexts, gameUiTexts} from 'data/ui-texts';
import {discussionQuestions} from 'data/discussion-data';
import Logo from 'components/ui/logo/logo';
import Button from 'components/ui/button/button';
import './discussion-group.scss';

const DiscussionGroup = ({game, group, schoolData, scenarioId, resetGameProgress, handleLogout, updateBackground}) => {

	/* Loading status */
	const [isLoading, setIsLoading] = useState(false);

	/* Get available questions and shuffle them */
	let availableQuestions = discussionQuestions;
	if (schoolData.questions) {
		availableQuestions = schoolData.questions;
	}
	const shuffledQuestions = useRef(shuffleArray(availableQuestions));

	/* Get question selected by group (if any) */
	const selectedQuestions = (game && game.selectedQuestions ? game.selectedQuestions : []);
	const groupSelectedQuestion = selectedQuestions.find((q) => {return q.groupId === group.id;});
	const groupSelectedQuestionData = (groupSelectedQuestion
		? availableQuestions.find((q) => {return q.id === groupSelectedQuestion.questionId;})
		: null
	);

	/**
	 * Select question
	 * @param {string} questionId 
	 * @returns 
	 */
	const handleSelectQuestion = (questionId) => {
		if (isLoading || groupSelectedQuestion) return;
		setIsLoading(true);
		apiHelper('group/select-question', {
			schoolId: schoolData.id,
			gameId: game.id, 
			groupId: group.id, 
			questionId
		}).then(() => {
			setIsLoading(false);
		}).catch((error) => {
			setIsLoading(false);
			console.error(error);
		});
	};

	useEffect(() => {
		// Set background image id
		updateBackground('login', scenarioId);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={'DiscussionGroup ' + scenarioId}>
			{/* Game title and school name */}
			<div className='DiscussionGroup-logoWrapper'>
				<Logo scenarioId={scenarioId} page="landing"/>
				{schoolData && <div className="DiscussionGroup-schoolName">
					<span>{schoolData.name}</span>
				</div>}
			</div>

			{/* No question selected */}
			{!groupSelectedQuestion && <div className="DiscussionGroup-instructions">
				<div className="DiscussionGroup-instructionsTitle">
					<span>{gameUiTexts.chooseQuestionTitle}</span>
				</div>
				<div className="DiscussionGroup-instructionsText">
					<span>{gameUiTexts.chooseQuestionText}</span>
				</div>
			</div>}
			{!groupSelectedQuestion && <div className="DiscussionGroup-questions">
				{shuffledQuestions.current.map((q, index) => {
					const isSelected = 
						!q.isUnlimited === true && 
						selectedQuestions.some((sq) => {return sq.questionId === q.id;});
					return (
						<div key={q.id}  className="DiscussionGroup-questionWrap">
							<div 
								className={'DiscussionGroup-question ' + scenarioId 
									+ ' index-' + index + (isSelected ? ' selected' : '')}
								onClick={() => {if (!isSelected) handleSelectQuestion(q.id);}}
							>
								<div className="DiscussionGroupo-questionText">
									<span>{q.text}</span>
								</div>
							</div>
						</div>
					);
				})}
			</div>}

			{/* Question selected */}
			{groupSelectedQuestion && <div className="DiscussionGroup-selectedQuestion">
				<span>{groupSelectedQuestionData.text}</span>
			</div>}

			{/* Logout button */}
			<div className="DiscussionGroup-logoutBtn">
				<Button 
					classes={['login']} 
					text={loginUiTexts.logout}
					onClick={() => {handleLogout();}}
				/>
			</div>

			{/* Reset game btn */}
			{(appConfig.env === 'development' || appConfig.env === 'test') && 
				<div className="DiscussionGroup-resetBtn">
					<Button 
						classes={['login']} 
						text={gameUiTexts.resetBtn}
						onClick={() => {resetGameProgress();}}
					/>
				</div>
			}
		</div>
	);
};

DiscussionGroup.propTypes = {
	game: PropTypes.object.isRequired,
	group: PropTypes.object.isRequired,
	schoolData: PropTypes.object,
	scenarioId: PropTypes.string.isRequired,
	resetGameProgress: PropTypes.func.isRequired,
	handleLogout: PropTypes.func.isRequired,
	updateBackground: PropTypes.func.isRequired
};

export default DiscussionGroup;
